/**
 * @module utils/fetch
 */
import { removeDirtyChars } from "utils/utils";
import has from "lodash/has";

/**
 * Wraps and sets a timeout for a fetch. When the timeout finishes before the fetch
 * a `timeout` error is thrown, otherwise the fetch will naturally resolve or reject
 * @param {Number} ms - the timeout in milliseconds
 * @param {Promise} promise - the fetch
 * @returns {Promise<Object|Error>}
 * @see https://stackoverflow.com/questions/46946380/fetch-api-request-timeout
 */
export function fetchTimeout(ms, promise) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      reject(new Error(`Fetch timeout exceeded: ${ms}ms.`));
    }, ms);
    promise.then(resolve, reject);
  });
}

/**
 * Validate a response has an "ok" status, and is in the range 200-299 inclusive.
 * @param {Response} response - to validate
 * @returns {Promise<Object|Error>}
 */
export function validStatus(response) {
  if (response.ok) {
    return Promise.resolve(response);
  }
  return Promise.reject(new Error(`${response.status} ${response.statusText}`));
}

/**
 * Validate a response is of JSON content type and return it.
 * @param {Response} response - to convert
 * @returns {Promise<Object|TypeError>}
 */
export function handleJSON(response) {
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("application/json") >= 0) {
    return Promise.resolve(response.json());
  }
  const t = new TypeError(`Expected 'application/json', but got: ${contentType}`);
  return Promise.reject(t);
}

/**
 * Validate a response is of text/* content type and return it.
 * @param {Response} response - to convert
 * @returns {Promise<Object|TypeError>}
 */
export function handleText(response) {
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.indexOf("text") >= 0) {
    return Promise.resolve(response.text());
  }
  const t = new TypeError(`Expected 'text/*', but got: ${contentType}`);
  return Promise.reject(t);
}

/**
 * Validate a PageBuilder feature render API response and returns clean HTML.
 * @param {Response} response - with rendering property to cleanup.
 * @returns {Promise<Object|Error>}
 */
export function handleRenderFeature(response) {
  if (has(response, "rendering")) {
    return Promise.resolve(removeDirtyChars(response.rendering));
  }
  const e = new Error("Expected 'rendering' property in PB Render Feature response.");
  return Promise.reject(e);
}
